import { component, type Context } from "picoapp";
import type A11yDialog from "a11y-dialog";

const DIALOG_ID = "find-in-store-dialog";
const WIDGET_MOUNT_ID = "store-locator-modal-widget";
const SMARTCOMMERCE_SCRIPTS = [
  "https://s3.us-west-2.amazonaws.com/static.smartcommerce.co/assets/client/65LKCR/js/smartcart_olipop_us_prod.js",
  "https://ss.click2cart.com/assets/js/smartcart_min.js",
] as const;

const WIDGET_READY_SELECTOR = ".sc-pl-product-locator__container";
const RENDER_POLL_INTERVAL_MS = 100;
const RENDER_POLL_TIMEOUT_MS = 15000;

let modalController: StoreLocatorModalController | null = null;
let pendingUpc: string | null = null;

function loadScript(src: string): Promise<void> {
  if (document.querySelector(`script[src="${src}"]`)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.type = "text/javascript";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
}

async function ensureSmartCommerceScripts(): Promise<void> {
  for (const src of SMARTCOMMERCE_SCRIPTS) {
    await loadScript(src);
  }

  await waitForProductLocatorUtilities();
}

function waitForProductLocatorUtilities(): Promise<void> {
  if (window.SCProductLocatorUtilities) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (window.SCProductLocatorUtilities) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= RENDER_POLL_TIMEOUT_MS) {
        window.clearInterval(intervalId);
        reject(
          new Error("SmartCommerce product locator utilities failed to load")
        );
      }
    }, RENDER_POLL_INTERVAL_MS);
  });
}

function waitForWidgetMount(
  mount: HTMLElement,
  timeoutMs = RENDER_POLL_TIMEOUT_MS
): Promise<void> {
  if (mount.querySelector(WIDGET_READY_SELECTOR)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (mount.querySelector(WIDGET_READY_SELECTOR)) {
        window.clearInterval(intervalId);
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(intervalId);
        reject(new Error("SmartCommerce widget failed to render"));
      }
    }, RENDER_POLL_INTERVAL_MS);
  });
}

function applyUpcToWidget(upc: string): HTMLElement {
  const mount = document.getElementById(WIDGET_MOUNT_ID);
  if (!mount) {
    throw new Error("Store locator widget mount not found");
  }

  mount.setAttribute("data-sc-upc", upc);
  mount.dataset.scUpc = upc;
  return mount;
}

class StoreLocatorModalController {
  private opening = false;

  constructor(
    private readonly _node: HTMLElement,
    private readonly context: Context
  ) {}

  private getDialog(): A11yDialog | undefined {
    return this.context.getState().dialogs[DIALOG_ID];
  }

  private waitForDialog(): Promise<A11yDialog> {
    const existingDialog = this.getDialog();
    if (existingDialog) {
      return Promise.resolve(existingDialog);
    }

    return new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const intervalId = window.setInterval(() => {
        const dialog = this.getDialog();
        if (dialog) {
          window.clearInterval(intervalId);
          resolve(dialog);
          return;
        }

        if (Date.now() - startedAt >= RENDER_POLL_TIMEOUT_MS) {
          window.clearInterval(intervalId);
          reject(
            new Error(`Store locator dialog "${DIALOG_ID}" is not registered`)
          );
        }
      }, RENDER_POLL_INTERVAL_MS);
    });
  }

  async open(upc: string): Promise<void> {
    if (!upc || this.opening) return;

    this.opening = true;

    try {
      const dialog = await this.waitForDialog();
      const mount = applyUpcToWidget(upc);
      const needsRender =
        !mount.querySelector(WIDGET_READY_SELECTOR) ||
        mount.dataset.lastUpc !== upc;

      dialog.show();

      if (needsRender) {
        mount.innerHTML = "";
        await ensureSmartCommerceScripts();
        window.SCProductLocatorUtilities?.renderAllProductLocators(true);
        await waitForWidgetMount(mount);
        mount.dataset.lastUpc = upc;
      }

      pendingUpc = null;
    } catch (error) {
      console.error(error);
    } finally {
      this.opening = false;
    }
  }
}

export function openStoreLocatorModal(upc: string): void {
  if (!upc) return;

  pendingUpc = upc;

  if (modalController) {
    void modalController.open(upc);
  }
}

export const storeLocatorModal = component((node: HTMLElement, context) => {
  modalController = new StoreLocatorModalController(node, context);

  void ensureSmartCommerceScripts().catch(() => {
    // Preload on idle; first click will retry if this fails.
  });

  if (pendingUpc) {
    const upc = pendingUpc;
    void modalController.open(upc);
  }

  return () => {
    if (modalController) {
      modalController = null;
    }
  };
});

export const storeLocatorTrigger = component((node: HTMLElement) => {
  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    const upc = node.dataset.productLocatorUpc;
    if (!upc) return;
    openStoreLocatorModal(upc);
  };

  node.addEventListener("click", handleClick);

  return () => {
    node.removeEventListener("click", handleClick);
  };
});
